/**
 * useBlock.ts
 *
 * Per-entity React selectors over the blockStore (Step 1–2 of
 * BLOCK_STORE_REDESIGN.md). Unlike useGlobalBlocks (re-renders every consumer
 * on any block change) and useLiveBlocks (full-workspace Dexie query per
 * mounted component), these subscribe to exactly one slice:
 *
 *   useBlock(id)            → re-renders only when THAT block changes
 *   useChildBlocks(parentId)→ re-renders only on membership/order changes of
 *                             that parent's children (not child content edits
 *                             — subscribe the child row with useBlock for that)
 *   usePages()              → re-renders only when a page-type block is
 *                             added/removed/changed (never on content edits
 *                             to non-page blocks)
 *
 * Coverage note (until the Step-5 root IDB feed lands): the store mirrors the
 * React block context, not IndexedDB. A block that lives only in IDB — not
 * yet hydrated into context — is invisible here, so migrating consumers keep
 * their existing cold-start IDB rescue paths.
 */

"use client";

import { useCallback, useSyncExternalStore } from "react";
import type { Block } from "@/types/block";
import { blockStore } from "../store/blockStore";

const EMPTY_BLOCKS: readonly Block[] = Object.freeze([]);

// Stable server-snapshot getters (SSR renders never touch the store).
const getServerBlock = (): Block | undefined => undefined;
const getServerBlocks = (): readonly Block[] => EMPTY_BLOCKS;

const NOOP_UNSUBSCRIBE = () => {};

/**
 * Subscribe to a single block. Returns undefined when the id is nullish or
 * the block is not (yet) in the store.
 */
export function useBlock(blockId: string | null | undefined): Block | undefined {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      blockId ? blockStore.subscribeBlock(blockId, onStoreChange) : NOOP_UNSUBSCRIBE,
    [blockId],
  );
  const getSnapshot = useCallback(
    () => (blockId ? blockStore.getBlock(blockId) : undefined),
    [blockId],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getServerBlock);
}

/**
 * Subscribe to a parent's ordered child blocks (derived from its blockIds).
 * Structural subscription: notifies on add/remove/reorder/reparent, NOT on a
 * child's content edits — render child rows with useBlock(childId) for that.
 */
export function useChildBlocks(parentId: string | null | undefined): readonly Block[] {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      parentId ? blockStore.subscribeChildren(parentId, onStoreChange) : NOOP_UNSUBSCRIBE,
    [parentId],
  );
  const getSnapshot = useCallback(
    () => (parentId ? blockStore.getChildBlocks(parentId) : EMPTY_BLOCKS),
    [parentId],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getServerBlocks);
}

/**
 * Subscribe to the set of page-type blocks. For the coarse whole-workspace
 * consumers (sidebar page lists) — recomputes when a page is created,
 * deleted, moved, or edited, never on content-block keystrokes.
 */
export function usePages(): readonly Block[] {
  const subscribe = useCallback(
    (onStoreChange: () => void) => blockStore.subscribePages(onStoreChange),
    [],
  );
  const getSnapshot = useCallback(() => blockStore.getPages(), []);
  return useSyncExternalStore(subscribe, getSnapshot, getServerBlocks);
}
